import { DateOnly } from '@if/core/date'
import { EPaymentFrequency } from '@if/core/types'
import { CalendarSimulationDto, EOperationType, LoanSummaryInformationResultDto } from '@src/api/loan/loan-dto'
import {
  transformCalendarActionFromDto,
  transformCalendarActionToDto,
  transformFullLoanFromDto,
  transformFullLoanToDto,
} from '@src/api/loan/loan-transformer'
import { AddFeeSchemaType } from '@src/components/LoanCalendar/EditFees/AddFee-schema'
import { getToday, getTimeNow } from '@src/services/DateTimeProvider'
import { SafePaymentActionDateService } from '@src/services/SafePaymentActionDateService'
import {
  EBankFailureCode,
  EFeeType,
  ELoanStatus,
  EOperationStatus,
  EPaymentMethod,
  getFeeTypeFromProduct,
} from '../constants'
import { FullLoan, PaymentConfig } from './loan-calendar'
import { CustomerPaymentOperation, Operation, OperationInformation } from './loan-operation'

export enum ECalendarSimulationAction {
  UpdateFrequency = 'NewFrequencyActionDto',
  MoveCalendar = 'MoveCalendarActionDto',
  Reset = 'ResetCalendarActionDto',
  EditPayments = 'EditPaymentsCalendarActionDto',
  ChangePaymentStatus = 'ChangePaymentStatusActionDto',
  AddFee = 'AddFeeActionDto',
  EditFees = 'EditFeesActionDto',
  ChangeRegularPmt = 'ChangeRegularPaymentActionDto',
  ChargeOff = 'ChargeOffActionDto',
  DeleteChargeOff = 'DeleteChargeOffActionDto',
}

interface BaseCalendarSimulationAction<TActionType extends ECalendarSimulationAction> {
  actionType: TActionType
}

// Action for updating the loan calendar simulation frequency

export interface UpdatePaymentFrequencyCalendarAction
  extends BaseCalendarSimulationAction<ECalendarSimulationAction.UpdateFrequency> {
  frequency: EPaymentFrequency
  firstPaymentDate: DateOnly
}

// Action for moving the loan calendar simulation

export interface MoveCalendarCalendarAction
  extends BaseCalendarSimulationAction<ECalendarSimulationAction.MoveCalendar> {
  fromDate: DateOnly
  toDate: DateOnly
  reason: string
  paymentToRaiseIds: number[]
}

// Action for editing payments in the loan calendar simulation

export interface EditPayment {
  operationId: number
  effectiveDate?: DateOnly // null values indicate no change
  amount?: number
  paymentMethod?: EPaymentMethod
  note?: string | null
  sendSmsReminder?: boolean
}

export interface AddPayment {
  effectiveDate: DateOnly
  amount: number
  paymentMethod: EPaymentMethod
  note?: string | null
  sendSmsReminder: boolean
}

export interface EditPaymentsCalendarAction
  extends BaseCalendarSimulationAction<ECalendarSimulationAction.EditPayments> {
  editedPayments: EditPayment[]
  newPayments: AddPayment[]
  deletedPaymentIds: number[]
}

export interface ChangePaymentStatusAction
  extends BaseCalendarSimulationAction<ECalendarSimulationAction.ChangePaymentStatus> {
  newStatus: EOperationStatus
  paymentIds: number[]
  nsfFee: number | undefined
  transactionDate: DateOnly | undefined
  nsfStatus: EBankFailureCode | undefined
}

export interface AddFeeAction extends BaseCalendarSimulationAction<ECalendarSimulationAction.AddFee> {
  effectiveDate: DateOnly
  amount: number
  feeType: EFeeType
  note?: string | null
}

/** A fee the calendar carries; editing it changes the invoice it comes from. */
export interface ExistingFee extends AddFeeSchemaType {
  operationId: number
}

/**
 * The fee an operation carries when it can be edited: an invoice in force with a single positive fee line, which
 * is what the backend accepts. Null for any other operation (a payment, the loan creation, a legacy credit).
 */
/** The fee an operation carries, whatever its status: a single positive fee line. */
export const getFee = (operation: Operation): ExistingFee | null => {
  if (operation._internalType !== EOperationType.InvoiceOperation) return null
  if (operation.lenderInvoices.length > 0 || operation.debtInvoices.length !== 1) return null

  const line = operation.debtInvoices[0]
  const feeType = getFeeTypeFromProduct(line.productId)
  if (!feeType || line.amount <= 0) return null

  return {
    operationId: operation.id,
    feeType,
    amount: line.amount,
    effectiveDate: operation.effectiveDate,
    note: operation.note ?? '',
  }
}

/** A fee in force, which can be edited or cancelled. */
export const getEditableFee = (operation: Operation): ExistingFee | null =>
  operation.operationStatusId === EOperationStatus.Cancelled ? null : getFee(operation)

/** A cancelled fee, which can be reapplied. */
export const getCancelledFee = (operation: Operation): ExistingFee | null =>
  operation.operationStatusId === EOperationStatus.Cancelled ? getFee(operation) : null

export interface EditFee {
  operationId: number
  effectiveDate?: DateOnly // null values indicate no change
  amount?: number
  note?: string | null
}

/** Edits, cancels or reapplies existing fees in place, completed ones included. A fee is never edited to 0: it is cancelled. */
export interface EditFeesAction extends BaseCalendarSimulationAction<ECalendarSimulationAction.EditFees> {
  editedFees: EditFee[]
  cancelledFeeIds: number[]
  reappliedFeeIds: number[]
}

export interface ChangeRegularPmtAction
  extends BaseCalendarSimulationAction<ECalendarSimulationAction.ChangeRegularPmt> {
  amount: number
  fromDate: DateOnly
}

export interface ChargeOffAction extends BaseCalendarSimulationAction<ECalendarSimulationAction.ChargeOff> {
  effectiveDate: DateOnly
}

/** Deletes a charge-off no accounting period has seen. */
export interface DeleteChargeOffAction
  extends BaseCalendarSimulationAction<ECalendarSimulationAction.DeleteChargeOff> {
  operationId: number
}

// Add other simulation actions as needed with an union
export type CalendarSimulationAction =
  | UpdatePaymentFrequencyCalendarAction
  | MoveCalendarCalendarAction
  | EditPaymentsCalendarAction
  | ChangePaymentStatusAction
  | AddFeeAction
  | EditFeesAction
  | ChangeRegularPmtAction
  | ChargeOffAction
  | DeleteChargeOffAction
  | BaseCalendarSimulationAction<ECalendarSimulationAction.Reset>

export interface SignedCalendarSimulation {
  signature: string
  simulation: CalendarSimulation
}

export interface ComputeCalendarSimulationAction extends Partial<SignedCalendarSimulation> {
  action: CalendarSimulationAction
}

export interface SpreadOverdueAmount {
  simulation: CalendarSimulation
  paymentsDate: DateOnly[]
}

export class CalendarSimulation {
  public readonly fullLoan: FullLoan
  public readonly actions: CalendarSimulationAction[]
  public readonly operationInformations: OperationInformation[]
  public readonly customerPaymentOperations: OperationInformation<CustomerPaymentOperation>[]
  public readonly simulationExpiresOn: Date
  public readonly recalculateTransactionFrom: DateOnly | null
  public readonly summaryInformation: LoanSummaryInformationResultDto
  public readonly nextTransientId: number

  private _cachedLastExecutedOperation: OperationInformation | null | undefined = undefined

  private constructor(
    fullLoan: FullLoan,
    actions: CalendarSimulationAction[],
    operationInformations: OperationInformation[],
    customerPaymentOperations: OperationInformation<CustomerPaymentOperation>[],
    simulationExpiresOn: Date,
    recalculateTransactionFrom: DateOnly | null,
    summaryInformation: LoanSummaryInformationResultDto,
    nextTransientId: number,
  ) {
    this.fullLoan = fullLoan
    this.actions = actions
    this.operationInformations = operationInformations
    this.customerPaymentOperations = customerPaymentOperations
    this.simulationExpiresOn = simulationExpiresOn
    this.recalculateTransactionFrom = recalculateTransactionFrom
    this.summaryInformation = summaryInformation
    this.nextTransientId = nextTransientId
  }

  public getRepaymentConfigAtDate(date: DateOnly): PaymentConfig {
    return this.fullLoan.paymentConfigs.findLast((pc) => pc.effectiveDate <= date)!
  }

  public getNextPaymentAfterDate(date: DateOnly): CustomerPaymentOperation | undefined {
    const allPaymentAfterDate = this.customerPaymentOperations.filter(
      (x) => x.isCustomerPaymentOperation() && x.operation.effectiveDate > date,
    )

    if (allPaymentAfterDate.length === 0) return undefined
    return allPaymentAfterDate[0].operation
  }

  public getAllLoanRepaymentConfigDates(): DateOnly[] {
    return this.fullLoan.paymentConfigs.map((pc) => pc.effectiveDate)
  }

  public getCurrentLoanStatus(): ELoanStatus | null {
    const [firstEntry, ...otherEntries] = this.fullLoan.loanStatusHistory
    if (firstEntry === undefined) return null
    return otherEntries.reduce((latestEntry, entry) => {
      return entry.id > latestEntry.id ? entry : latestEntry
    }, firstEntry).loanStatusId
  }

  public getLastExecutedOperation(): OperationInformation | null {
    if (this._cachedLastExecutedOperation == undefined) {
      this._cachedLastExecutedOperation = this.operationInformations.findLast((oi) => oi.isExecuted()) || null
    }

    return this._cachedLastExecutedOperation
  }

  public getLastPaymentDate(): DateOnly {
    return this.customerPaymentOperations.length > 0
      ? this.customerPaymentOperations[this.customerPaymentOperations.length - 1].operation.effectiveDate
      : this.fullLoan.loan.lastPaymentOn
  }

  public getThreeNextPlannedPayments(): CustomerPaymentOperation[] {
    return this.customerPaymentOperations
      .filter(
        (operationInfo) =>
          operationInfo.operation.operationStatusId === EOperationStatus.Planned &&
          operationInfo.operation.effectiveDate >= getToday(),
      )
      .slice(0, 3)
      .map((operationInfo) => operationInfo.operation)
  }

  public getNearestOperationInformation() {
    const today = getToday()
    return this.operationInformations.findLast(
      (oi) => oi.operation.effectiveDate && oi.operation.effectiveDate <= today,
    )
  }

  public hasExecutedPaymentsAfterDate(date: Date): boolean {
    return this.operationInformations.some(
      (oi) => oi.isCustomerPaymentOperation() && oi.isExecuted() && oi.operation.effectiveDate >= date,
    )
  }

  public getTotalFundedProductsAmount(): number {
    return this.fullLoan.loan.fundedProducts.reduce((total: number, product) => total + product.amount, 0)
  }

  /**
   * Get the next valid action date based on the payment method and executed operations
   * Any batch action done on the simulation must be after this date
   * Since you cannot do actions in the past (or before the minimum delay for the payment method) or on executed payments
   * @param paymentMethod The payment method to get the next valid action date for
   * @returns The next valid action date
   */
  public getNextValidActionDate(
    paymentMethod: EPaymentMethod = EPaymentMethod.PreAuthorizedDebit,
    canBypassAcpLock = false,
  ): DateOnly {
    const minimumDate = SafePaymentActionDateService.getInstance().getFirstSafeDate(paymentMethod, canBypassAcpLock)
    const lastExecutedOperationDate = this.getLastExecutedOperation()?.operation.effectiveDate
    if (lastExecutedOperationDate && lastExecutedOperationDate > minimumDate) return lastExecutedOperationDate
    return minimumDate
  }

  /**
   * Check if the simulation has expired based on its TTL
   * A simulation is expired when the current time exceeds simulationExpiresOn
   *
   * @param now - Optional current time for testing (defaults to Date.now())
   * @returns true if the simulation has expired, false if it's still valid
   */
  public isExpired(now: Date = getTimeNow()): boolean {
    return this.simulationExpiresOn.getTime() <= now.getTime()
  }

  public static fromDto(calendarSimulationDto: CalendarSimulationDto): CalendarSimulation {
    const {
      fullLoan,
      actions,
      operationInformations,
      simulationExpiresOn,
      recalculateTransactionFrom,
      loanSummaryInformation: summaryInformation,
      nextTransientId,
    } = calendarSimulationDto
    const customerPaymentOperations: OperationInformation<CustomerPaymentOperation>[] = []
    const operationInfos: OperationInformation[] = []
    let paymentNumberIndex = 1

    for (const info of operationInformations) {
      let oi: OperationInformation
      if (info.operation._internalType === EOperationType.CustomerPaymentOperation) {
        oi = OperationInformation.fromDto(info, paymentNumberIndex++)
        customerPaymentOperations.push(oi as OperationInformation<CustomerPaymentOperation>)
      } else {
        oi = OperationInformation.fromDto(info)
      }
      operationInfos.push(oi)
    }

    return new CalendarSimulation(
      transformFullLoanFromDto(fullLoan),
      actions.map((actionDto) => transformCalendarActionFromDto(actionDto)),
      operationInfos,
      customerPaymentOperations,
      new Date(simulationExpiresOn),
      recalculateTransactionFrom ? new DateOnly(recalculateTransactionFrom) : null,
      summaryInformation,
      nextTransientId,
    )
  }

  public static toDto(calendarSimulation: CalendarSimulation): CalendarSimulationDto {
    return {
      fullLoan: transformFullLoanToDto(calendarSimulation.fullLoan),
      actions: calendarSimulation.actions.map((action) => transformCalendarActionToDto(action)),
      operationInformations: calendarSimulation.operationInformations.map((info) => OperationInformation.toDto(info)),
      simulationExpiresOn: calendarSimulation.simulationExpiresOn.toISOString(),
      recalculateTransactionFrom: calendarSimulation.recalculateTransactionFrom
        ? new DateOnly(calendarSimulation.recalculateTransactionFrom).toString()
        : null,
      loanSummaryInformation: calendarSimulation.summaryInformation,
      nextTransientId: calendarSimulation.nextTransientId,
    }
  }
}
